import AddIcon from '@mui/icons-material/Add';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';

import useBatchDelete from '../bg_tasks/useBatchDelete';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import DeleteButton from '../components/DeleteButton';
import DeleteIconButton from '../components/DeleteIconButton';
import BusTourListMobileLayout, { BusTourMobileActionButton, BusTourMobileListItem } from './BusTourListMobile';
import BusTourListSearchField, { busTourListToolbarButtonSx, busTourListToolbarRowSx } from './BusTourListSearchField';
import { useBusTourAddLabel } from './useBusTourAddLabel';

const useStyles = makeStyles((theme) => ({
    recordList: {
        height: `calc(${theme.cp.contentHeight} - 6rem) !important`,
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
}));

const LineSetList = (props) => {
    const history = useHistory();
    const { hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const classes = useStyles();
    const isSmallScreen = !useIsWidthUp('sm');

    const [syncActive, setSyncActive] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const urlParams = new URLSearchParams(window.location.search);
    const [query, setQuery] = React.useState(urlParams.get('query') || null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const addButtonLabel = useBusTourAddLabel('NEW LINE SET');

    const resource = 'route_set';

    const onQueryChange = (newQuery) => {
        setQuery(newQuery);
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('query', newQuery);
        history.push(`${window.location.pathname}?${searchParams.toString()}`);
    };

    // CP-7771: bulk delete runs as a background task; the grid refreshes once it has finished.
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['line set', 'line sets'], onDeleted: refreshList });
    const doDelete = (idlist) => {
        setSyncActive(true);
        deleteRecords(idlist)
            .then((accepted) => {
                if (accepted) {
                    setSelectedIds([]);
                    setIdsToDelete(null);
                }
            })
            .finally(() => setSyncActive(false));
    };

    const listItemRenderer = React.useCallback(
        (row, idx, rows) => {
            const openRow = () => history.push(`/bus-tours/line-sets/${row.id}/edit`);
            const tags = row.tags?.map((x) => x.name).join(', ');
            const atags = row.atag?.map((x) => x.name).join(', ');

            return (
                <BusTourMobileListItem
                    key={row.id}
                    name={row.name}
                    onOpen={openRow}
                    isLast={idx >= rows.length - 1}
                    details={
                        <>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Line Set ID: {row.id}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Tags: {tags || '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Access Groups: {atags || '—'}
                            </Typography>
                        </>
                    }
                    actions={
                        <>
                            <Grid item>
                                <IconButton
                                    size="small"
                                    component={RouterLink}
                                    to={`/bus-tours/line-sets/${row.id}/edit`}
                                    onClick={(ev) => ev.stopPropagation()}
                                    aria-label="Edit line set"
                                >
                                    <BorderColorOutlinedIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <DeleteIconButton
                                    title="Delete line set"
                                    perm="vehicle_fleet.delete"
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        setIdsToDelete([row.id]);
                                    }}
                                />
                            </Grid>
                        </>
                    }
                />
            );
        },
        [history]
    );

    const lineSetsRecordList = (
        <RecordList
            className={classes.recordList}
            resource={resource}
            disableSelectionOnClick
            sortModel={[{ field: 'id', sort: 'desc' }]}
            deps={lastUpdate}
            query={query}
            selectionModel={selectedIds}
            onSelectionModelChange={(selectionModel, selection) => setSelectedIds(selection || [])}
            listItemRenderer={isSmallScreen ? listItemRenderer : undefined}
            mobileNaturalHeight={isSmallScreen}
            noMatchesLabel="No line sets"
            noItemsFoundMessage="No line sets"
            columns={[
                ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                {
                    field: 'id',
                    headerName: 'Line Set ID',
                    sortable: true,
                    width: 120,
                },
                {
                    field: 'name',
                    headerName: 'Name',
                    sortable: true,
                    flex: 1,
                },
                {
                    field: 'tags',
                    headerName: 'Tags',
                    sortable: false,
                    flex: 0.7,
                    valueGetter: (p) => p.row.tags?.map((x) => x.name).join(', '),
                },
                {
                    field: 'atag',
                    headerName: 'Access Groups',
                    sortable: false,
                    flex: 0.5,
                    valueGetter: (p) => p.row.atag?.map((x) => x.name).join(', '),
                },
                {
                    field: 'buttons',
                    headerName: 'Action',
                    sortable: false,
                    width: 100,
                    renderCell: (p) => (
                        <Grid container alignItems="center">
                            <Grid item>
                                <IconButton size="small" component={RouterLink} to={`/bus-tours/line-sets/${p.id}/edit`} onClick={(e) => e.stopPropagation()}>
                                    <EditIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <DeleteIconButton perm="vehicle_fleet.delete" onClick={() => setIdsToDelete([p.id])} />
                            </Grid>
                        </Grid>
                    ),
                },
            ]}
        />
    );

    return (
        <>
            <PageMeta title="Line Sets" />
            {idsToDelete && (
                <ConfirmationDialog
                    keepMounted
                    open={!!idsToDelete}
                    onCancel={() => setIdsToDelete(null)}
                    onConfirm={() => doDelete(idsToDelete)}
                    title={idsToDelete.length === 1 ? 'Delete line set?' : `Delete ${idsToDelete.length} line sets?`}
                    okButtonText="Delete"
                    cancelButtonText="Cancel"
                >
                    {idsToDelete.length === 1
                        ? 'Are you sure you want to delete this line set? This will also remove all associated routes.'
                        : `Are you sure you want to delete ${idsToDelete.length} line sets? This will also remove all associated routes.`}
                </ConfirmationDialog>
            )}
            <FixedLinearProgress visibility={syncActive} />
            {isSmallScreen ? (
                <BusTourListMobileLayout
                    title="Line Sets"
                    searchLabel="Search Line Sets"
                    searchValue={query || ''}
                    onSearchChange={(e) => onQueryChange(e.target.value)}
                    actionButton={
                        <BusTourMobileActionButton
                            selectedCount={selectedIds.length}
                            canDelete={hasPerm('vehicle_fleet', 'delete')}
                            onDelete={() => setIdsToDelete(selectedIds)}
                            onAdd={() => history.push('/bus-tours/line-sets/new')}
                            addLabel={addButtonLabel}
                            deleteLabel="Delete selected line sets"
                        />
                    }
                >
                    {lineSetsRecordList}
                </BusTourListMobileLayout>
            ) : (
                <Grid container direction="row" spacing={2} p={2}>
                    <Grid item xs={12} sx={{ pb: '0 !important' }}>
                        <PageHeading sx={{ mb: 1.5 }}>Line Sets</PageHeading>
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '0 !important' }}>
                        <Box sx={busTourListToolbarRowSx}>
                            <BusTourListSearchField
                                value={query || ''}
                                onChange={(e) => onQueryChange(e.target.value)}
                            />
                            {selectedIds.length > 0 ? (
                                <DeleteButton
                                    perm="vehicle_fleet.delete"
                                    onClick={() => setIdsToDelete(selectedIds)}
                                    sx={busTourListToolbarButtonSx}
                                >
                                    {selectedIds.length === 1 ? 'DELETE LINE SET' : `DELETE ${selectedIds.length} LINE SETS`}
                                </DeleteButton>
                            ) : (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => history.push('/bus-tours/line-sets/new')}
                                    sx={busTourListToolbarButtonSx}
                                >
                                    {addButtonLabel}
                                </Button>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        {lineSetsRecordList}
                    </Grid>
                </Grid>
            )}
        </>
    );
};

export default LineSetList;
